import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  getBadges,
  getLockedBadges,
  pinBadge,
  evaluateBadges,
} from "../../api/user";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faTimes, faThumbTack, faTrophy } from "@fortawesome/free-solid-svg-icons";
import CoinHistoryModal from "../Other/CoinHistoryModal";

/* -------------------------------------------------------------------------- */
/*  Constants                                                                   */
/* -------------------------------------------------------------------------- */
/* Trophy Case shows three pinned badges (server: BadgeController::PIN_LIMIT). */
const PIN_LIMIT = 3;

/**
 * Empty state pairs these two Founding Era badges (per design 14473). Falls back to
 * whatever the locked-badge endpoint returns, in prestige order, if either is missing
 * — e.g. once the viewer has already earned one of them.
 */
const EMPTY_STATE_SLUGS = ["league_zero", "charter_trader"];

const pickEmptyStateBadges = (list = []) => {
  const bySlug = new Map(list.map((b) => [b.slug, b]));
  const picked = EMPTY_STATE_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
  for (const b of list) {
    if (picked.length >= 2) break;
    if (!picked.includes(b)) picked.push(b);
  }
  return picked.slice(0, 2);
};

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                              */
/* -------------------------------------------------------------------------- */

/* Heading gold, a shade deeper than the badge-art gold (#D7C588) so it does not wash out. */
const HEADING_GOLD = "#B8963E";

const TrophyIconSvg = () => (
  <FontAwesomeIcon icon={faTrophy} className="shrink-0 mr-2 text-[17px]" style={{ color: HEADING_GOLD }} />
);

/* Padlock bridging the two locked badges. Inline so it inherits no external CSS
   context — /lock-icon-black.svg relies on currentColor and cannot render via <img>. */
const LockGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4.5" y="10" width="15" height="10.5" rx="2.6" fill="#D2D5DA" stroke="#9AA0A6" strokeWidth="1.1" />
    <path d="M8.25 10V7.4a3.75 3.75 0 017.5 0V10" stroke="#9AA0A6" strokeWidth="1.9" strokeLinecap="round" />
    <circle cx="12" cy="14.4" r="1.45" fill="#6B7280" />
    <path d="M12 15.5v2.2" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* -------------------------------------------------------------------------- */
/*  Background scroll lock                                                      */
/* -------------------------------------------------------------------------- */

/* Open layers (View All, badge detail), innermost last. Only the top layer decides what may
   scroll, so with the detail open on top of View All, View All is frozen too. */
const scrollLayers = [];
let savedOverflow = null;

/**
 * Locks the page behind a modal. `overflow: hidden` alone does not stop iOS (Safari or the
 * app's webview) from scrolling the page under a fixed overlay, so touchmove/wheel outside
 * the layer's own scroll area are cancelled as well — same approach as ResetPortfolioModal.
 * Nothing moves the page, so closing returns to the exact scroll position.
 */
const useScrollLock = (scrollRef = null) => {
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const layer = {};
    if (!scrollLayers.length) {
      savedOverflow = { body: body.style.overflow, html: html.style.overflow };
      body.style.overflow = "hidden";
      html.style.overflow = "hidden";
    }
    scrollLayers.push(layer);

    const block = (e) => {
      if (scrollLayers[scrollLayers.length - 1] !== layer) return;
      if (scrollRef?.current?.contains(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener("touchmove", block, { passive: false });
    document.addEventListener("wheel", block, { passive: false });

    return () => {
      document.removeEventListener("touchmove", block);
      document.removeEventListener("wheel", block);
      scrollLayers.splice(scrollLayers.indexOf(layer), 1);
      if (!scrollLayers.length && savedOverflow) {
        body.style.overflow = savedOverflow.body;
        html.style.overflow = savedOverflow.html;
        savedOverflow = null;
      }
    };
  }, [scrollRef]);
};

const EmptyStateGraphic = ({ lockedBadges = [] }) => {
  const preview = pickEmptyStateBadges(lockedBadges);
  return (
    <div className="relative flex flex-col items-center justify-center py-4 px-2 select-none">
      <div className="relative flex items-center justify-center my-1">
        {/* Soft glow behind the pair — real depth, no invented geometry */}
        <div className="absolute w-48 h-48 md:w-56 md:h-56 bg-[#D7C588]/20 rounded-full blur-3xl pointer-events-none" />

        {preview.length ? (
          <>
            {/* Gap between the badges is sized so the padlock lands between them,
                overlapping only their inner edges — never the face artwork. */}
            {preview.map((b, idx) => (
              <div
                key={b.slug || idx}
                className={`relative w-[104px] h-[104px] md:w-[124px] md:h-[124px] ${
                  idx === 0 ? "mr-3.5 md:mr-4" : "ml-3.5 md:ml-4"
                }`}
              >
                <img
                  src={b.image}
                  alt={b.name}
                  title={b.name}
                  className="w-full h-full object-contain grayscale-[0.55] opacity-80 drop-shadow-[0_6px_10px_rgba(0,0,0,0.18)]"
                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                />
              </div>
            ))}

            {/* Padlock sits between the badges, glow behind it */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
              <div className="absolute w-12 h-12 rounded-full bg-white/70 blur-md" />
              <div className="relative w-11 h-11 md:w-12 md:h-12 rounded-full bg-white shadow-[0_3px_10px_rgba(0,0,0,0.16)] ring-1 ring-gray-200/80 flex items-center justify-center">
                <LockGlyph className="w-6 h-6 md:w-[26px] md:h-[26px]" />
              </div>
            </div>
          </>
        ) : (
          <div className="w-[104px] h-[104px] rounded-full bg-gray-100" />
        )}
      </div>

      <div className="mt-4 text-center max-w-xs">
        <p className="text-xs font-bold text-gray-900 font-poppins mb-1">No badges yet</p>
        <p className="text-[11px] text-gray-500 font-poppins leading-normal">
          Your first filled trade earns your first badge and locks in your Founding Era place.
        </p>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Badge detail — one compact card on desktop and mobile (client, 2026-10-22)  */
/* -------------------------------------------------------------------------- */
/**
 * Badge art first, tagline second, muted issue number third (founder bands only). The owner gets an understated
 * pin action; visitors see the same card without it. Close with the X or a click/tap
 * outside. When the case is full, pinning asks which pinned badge to replace.
 */
const BadgeDetailModal = ({ badge, isOwn, pinnedBadges, onClose, onPin }) => {
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  useScrollLock();

  const caseFull = !badge.is_pinned && pinnedBadges.length >= PIN_LIMIT;

  const pin = async (replaceUuid = null) => {
    if (busy) return;
    setBusy(true);
    const ok = await onPin(badge, replaceUuid);
    setBusy(false);
    if (ok) setChoosing(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 px-6" onClick={onClose}>
      <div
        className="relative w-full max-w-[340px] md:max-w-[380px] bg-white rounded-3xl px-6 md:px-8 pt-10 pb-7 shadow-2xl text-center"
        style={{ animation: "detailIn 0.2s cubic-bezier(0.34,1.3,0.64,1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-sm transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <img
          src={badge.image}
          alt={badge.name}
          className="mx-auto w-[72%] max-w-[240px] aspect-square object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.18)]"
        />

        {badge.tagline && (
          <p
            className="mt-6 text-lg font-medium font-poppins leading-snug text-[#1A1A1A]"
            style={{ textWrap: "pretty" }}
          >
            {badge.tagline}
          </p>
        )}

        {/* Only the four founder bands send one: the platform-wide trader position. Every
            other badge already carries its number on the face. */}
        {badge.modal_issue_number && (
          <p className="mt-2 text-[11px] font-medium font-poppins tracking-[0.2em] text-gray-400 uppercase">
            Issue #{badge.modal_issue_number}
          </p>
        )}

        {isOwn && !choosing && (
          <button
            type="button"
            disabled={busy}
            onClick={() => (caseFull ? setChoosing(true) : pin())}
            className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-poppins text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-60"
          >
            <FontAwesomeIcon
              icon={faThumbTack}
              className={`text-[11px] ${badge.is_pinned ? "text-primaryColor" : ""}`}
            />
            {badge.is_pinned ? "Unpin from Trophy Case" : "Pin to Trophy Case"}
          </button>
        )}

        {isOwn && choosing && (
          <div className="mt-5">
            <p className="text-xs font-poppins text-gray-500">Your Trophy Case is full. Replace one:</p>
            <div className="mt-3 flex items-center justify-center gap-3">
              {pinnedBadges.map((p) => (
                <button
                  key={p.uuid}
                  type="button"
                  disabled={busy}
                  onClick={() => pin(p.uuid)}
                  aria-label={`Replace ${p.name}`}
                  className="w-16 h-16 rounded-xl p-1 hover:bg-gray-100 transition-colors disabled:opacity-60"
                >
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setChoosing(false)}
              className="mt-2 text-xs font-poppins text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes detailIn {
          from { transform: scale(0.95); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

/* -------------------------------------------------------------------------- */
/*  View All Badges                                                             */
/* -------------------------------------------------------------------------- */
/**
 * The badge detail opens on top of this modal (client OK'd stacking, 2026-10-22). This one
 * stays mounted underneath, so closing the detail returns to the same scroll position.
 */
const ViewAllBadgesModal = ({ badges, onClose, onOpen }) => {
  const scrollRef = useRef(null);
  useScrollLock(scrollRef);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[380px] md:max-w-[840px] bg-white rounded-2xl flex flex-col shadow-2xl"
        style={{ animation: "modalIn 0.22s cubic-bezier(0.34,1.3,0.64,1) both", maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — always visible, never scrolls */}
        <div className="flex items-center justify-between px-6 md:px-7 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <TrophyIconSvg />
            <div>
              <h3 className="text-lg md:text-xl font-extrabold font-montserrat leading-tight" style={{ color: HEADING_GOLD }}>
                Trophy Case
              </h3>
              <p className="text-xs text-gray-400 font-poppins mt-0.5">
                {badges.length} {badges.length === 1 ? "badge" : "badges"} earned
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors text-lg"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Badge grid — scrolls inside the modal; the page behind stays put. */}
        <div ref={scrollRef} className="badge-scroll overflow-y-auto overscroll-contain p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {badges.map((b) => (
              <button
                key={b.uuid}
                type="button"
                aria-label={b.name}
                onClick={() => onOpen(b)}
                className="group relative w-full flex items-center justify-center rounded-2xl p-2 cursor-pointer"
              >
                {/* Fluid so it fills the cell on any viewport (never overflows a narrow
                    phone) while capping at a size where the badge's own face text reads. */}
                <div className="relative w-full max-w-[140px] md:max-w-[156px] aspect-square">
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-full h-full object-contain transition-all duration-200 ease-out
                      group-hover:-translate-y-1 group-hover:scale-[1.03] group-hover:drop-shadow-[0_10px_18px_rgba(0,0,0,0.22)]"
                    onError={(e) => { e.target.style.visibility = "hidden"; }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { transform: scale(0.96); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        .badge-scroll::-webkit-scrollbar { width: 5px; }
        .badge-scroll::-webkit-scrollbar-track { background: transparent; }
        .badge-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 999px; }
        .badge-scroll { scrollbar-width: thin; scrollbar-color: #E5E7EB transparent; }
      `}</style>
    </div>,
    document.body
  );
};

/* -------------------------------------------------------------------------- */
/*  Main TrophyCase Component                                                   */
/* -------------------------------------------------------------------------- */
const TrophyCase = ({ userId = null, isOwn = false, coins = 0, coinsGuideId }) => {
  const [badges, setBadges] = useState([]);
  const [lockedBadges, setLockedBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // badge shown in the detail card
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [showCoinHistory, setShowCoinHistory] = useState(false);

  /* ---- Data Loading ---- */
  const load = async () => {
    try {
      setLoading(true);
      if (isOwn) {
        try { await evaluateBadges(); } catch (e) { /* non-fatal */ }
      }
      const data = await getBadges(userId);
      const list = Array.isArray(data) ? data : [];
      setBadges(list);
      if (!list.length) {
        try {
          const locked = await getLockedBadges(userId);
          setLockedBadges(Array.isArray(locked) ? locked : []);
        } catch (e) {
          setLockedBadges([]);
        }
      }
    } catch (e) {
      setBadges([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  // A "Badge earned" notification opens /profile?trophy=1. Profile mounts one
  // Trophy Case per layout and hides the other, so only the visible one scrolls.
  const rootRef = useRef(null);
  const { search } = useLocation();
  useEffect(() => {
    if (loading || !isOwn || new URLSearchParams(search).get("trophy") !== "1") return;
    const el = rootRef.current;
    if (el && el.offsetParent !== null) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, isOwn, search]);

  /* ---- Pin ---- */
  // replaceUuid: the pinned badge this one takes the slot of (case full). Returns success.
  const handlePin = async (badge, replaceUuid = null) => {
    try {
      const next = !badge.is_pinned;
      const res = await pinBadge(badge.uuid, next, replaceUuid);
      if (res?.success === false) { toast.error(res.message); return false; }
      toast.success(res?.message || "Updated");
      const slot = replaceUuid ? badges.find((b) => b.uuid === replaceUuid)?.pin_order ?? null : undefined;
      const apply = (b) => {
        if (b.uuid === badge.uuid) return { ...b, is_pinned: next, ...(slot !== undefined ? { pin_order: slot } : {}) };
        if (replaceUuid && b.uuid === replaceUuid) return { ...b, is_pinned: false, pin_order: null };
        return b;
      };
      setBadges((prev) => prev.map(apply));
      setSelected((s) => (s ? apply(s) : s));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not update pin");
      return false;
    }
  };

  /* ---- Render ---- */
  if (loading) {
    return (
      <div className="mb-4 w-full bg-white rounded-2xl border border-[#F3F4F6] p-5 shadow-sm flex items-center justify-center min-h-[120px]">
        <div className="flex flex-col items-center gap-2 text-gray-300">
          <div className="w-6 h-6 border-2 border-[#D7C588] border-t-transparent rounded-full animate-spin" />
          <span className="text-[11px] font-poppins text-gray-400">Loading Trophy Case…</span>
        </div>
      </div>
    );
  }

  const displayBadges = [...badges].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
    return (a.pin_order ?? 99) - (b.pin_order ?? 99);
  });
  const topRowBadges = displayBadges.slice(0, 3);
  const pinnedBadges = displayBadges.filter((b) => b.is_pinned);

  return (
    <div ref={rootRef} className="mb-4 w-full bg-white rounded-2xl border border-[#F3F4F6] p-5 shadow-sm relative overflow-visible">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <TrophyIconSvg />
          <h3 className="text-[15px] font-extrabold font-montserrat" style={{ color: HEADING_GOLD }}>Trophy Case</h3>
        </div>
        {badges.length >= 4 && (
          <button
            type="button"
            onClick={() => setShowViewAllModal(true)}
            className="text-[11px] font-bold text-primaryColor hover:underline"
          >
            View all ›
          </button>
        )}
      </div>

      {/* ── Empty State ── */}
      {!badges.length ? (
        <EmptyStateGraphic lockedBadges={lockedBadges} />
      ) : (
        /* ── Active State ── */
        <div className="py-2">
          {/* Three badges use the whole case: the row runs into the card's side padding and
              the gaps stay tight, so the art grows without the card getting any wider. */}
          <div
            className={`my-2 ${
              topRowBadges.length === 3
                ? "grid grid-cols-3 gap-1 -mx-3"
                : topRowBadges.length === 2
                ? "flex items-center justify-center gap-4"
                : "flex items-center justify-center"
            }`}
          >
            {topRowBadges.map((b) => (
              <button
                key={b.uuid}
                type="button"
                aria-label={b.name || "Badge"}
                className="relative group flex flex-col items-center cursor-pointer min-w-0"
                onClick={() => setSelected(b)}
              >
                {/* Hover only lifts the badge — no tagline, no tooltip (client, 2026-10-22). */}
                <div className="w-full flex justify-center transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:scale-[1.03]">
                  <img
                    src={b.image}
                    alt={b.name || "Badge"}
                    // No background and no box-shadow: the badge PNG is transparent, so a
                    // white fill or a box-shadow would draw a square (and its four corners)
                    // around the octagon. drop-shadow follows the artwork's own alpha shape.
                    className={`object-contain transition-[filter,transform] duration-200 group-hover:drop-shadow-[0_10px_18px_rgba(0,0,0,0.22)] ${
                      topRowBadges.length === 1
                        ? "h-[132px] w-[132px]"
                        : topRowBadges.length === 2
                        ? "h-[120px] w-[120px]"
                        : "w-full max-w-[120px] aspect-square"
                    }`}
                    onError={(e) => { e.target.style.visibility = "hidden"; }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Merged QUSHi Coins (private profile only) ── */}
      {isOwn && (
        <>
          <div className="border-t border-[#F3F4F6] my-3" />
          <div
            id={coinsGuideId}
            role="button"
            tabIndex={0}
            onClick={() => setShowCoinHistory(true)}
            className="flex items-center gap-4 cursor-pointer hover:bg-gray-50/70 px-2 py-2.5 rounded-xl transition-colors"
          >
            <div className="shrink-0 w-14 h-14 flex items-center justify-center">
              <img
                src="/qushi-coins-icon-com.png"
                alt="QUSHi Coins"
                className="w-14 h-14 object-contain"
                onError={(e) => { e.target.src = "/doublecircleicon.png"; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 font-montserrat leading-tight">QUSHi Coins</p>
              <p className="text-[28px] leading-tight font-black text-primaryColor font-poppins mt-0.5">
                {Number(coins || 0).toLocaleString("en-US")}
              </p>
              <p className="text-[11px] text-gray-500 leading-snug font-poppins mt-0.5">
                Earned through activity, lessons, contests, and referrals.
              </p>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="text-[#C9CCD1] shrink-0 text-base" />
          </div>

          <CoinHistoryModal open={showCoinHistory} onClose={() => setShowCoinHistory(false)} />
        </>
      )}

      {/* ── View All Modal ── */}
      {showViewAllModal && (
        <ViewAllBadgesModal
          badges={badges}
          onClose={() => setShowViewAllModal(false)}
          onOpen={setSelected}
        />
      )}

      {/* ── Badge detail (desktop and mobile; on top of View All when opened from it) ── */}
      {selected && (
        <BadgeDetailModal
          badge={selected}
          isOwn={isOwn}
          pinnedBadges={pinnedBadges}
          onClose={() => setSelected(null)}
          onPin={handlePin}
        />
      )}
    </div>
  );
};

export default TrophyCase;
